import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { Select, Slider } from "antd";
import { EVA_RESEARCH_NAME, IS_MCDAN_ENV } from "constants/app.constants";
import { RADIUS_DIMENSION_MAX } from "constants/visualization.constants";
import { selectXdaLineRequest, useMapStore } from "stores/map";

import getGeoMapLayers from "components/geo-map/hooks/getGeoMapLayers";
import { useUserSettings } from "components/user/hooks";

import { useXdaSettingsStore } from "../../context";
import { XDASettingsT } from "../../context/types";
import { selectXdaSettings } from "../../context/xdaSettingsSelectors";
import { XdaOptionItem, XdaOptionLabel, XdaOptionsWrapper } from "./XdaOptionsLayout";

interface XdaSettingsOptionsProps {
  // Reactive list of plays returned by the XDA API. Passed as state (not read
  // off dataRef) so play-selection logic re-runs when a new region's plays load.
  availablePlays: string[];
  isLoading: boolean;
  // Passed from XdaViewer so both components use the same definition (which
  // includes xdaLineRequest.wells for group-mode correctness).
  noWellsAndSticks: boolean;
}
const XdaSettingsOptions = ({
  availablePlays,
  isLoading,
  noWellsAndSticks
}: XdaSettingsOptionsProps) => {
  const settings = useXdaSettingsStore(useShallow(selectXdaSettings));
  const setXdaSettings = useXdaSettingsStore((s) => s.setXdaSettings);
  const { topsModelSource, widthScaled } = settings;

  const userSettings = useUserSettings();

  const { data: mapLayers } = getGeoMapLayers();

  const mcdanielGroup = [
    {
      label: EVA_RESEARCH_NAME,
      options: [{ label: EVA_RESEARCH_NAME, value: "McDaniel" }]
    }
  ];
  const [topsModelSources, setTopsModelSources] = useState(mcdanielGroup);
  const [localSelectedPlays, setLocalSelectedPlays] = useState([]);
  // The drawn cross-section line identifies the current region. Selecting a play
  // re-fetches on the SAME line, so the line key stays stable through it — unlike
  // available_plays, which the service grows once a play is selected.
  const xdaLineRequest = useMapStore(selectXdaLineRequest);
  const regionKey = JSON.stringify(xdaLineRequest?.line ?? []);
  // Last region (line) we ran the play-selection logic for, so we only auto-select
  // or clear when the user genuinely moves to a new region.
  const prevRegionKeyRef = useRef("");

  useEffect(() => {
    // A genuine "new region" is signalled by the drawn line changing — NOT by
    // available_plays changing. The XDA service returns a different (larger)
    // available_plays once a play is selected (it queries all zones to keep the
    // dropdown fully populated), so keying region-change off available_plays
    // content made selecting a play look like a region change and wiped the
    // user's selection. The drawn line is stable across a selection round-trip.
    //
    // regionKey is read here but deliberately NOT a dep: the line updates the
    // instant it's drawn, BEFORE the /line response refreshes availablePlays.
    // If the effect fired on regionKey it would consume regionChanged=true while
    // availablePlays still held the PREVIOUS region's plays, then run again on
    // the real response with regionChanged=false — so auto-select and the
    // multi-play reset would never see the new region. Gating on availablePlays
    // instead means the effect runs when the response lands, by which point the
    // live regionKey already reflects the new line.
    const regionChanged = regionKey !== prevRegionKeyRef.current;
    prevRegionKeyRef.current = regionKey;

    // Auto-select the sole available play only when entering a new region
    // (regionChanged) so that manually removing the play doesn't get undone by
    // the subsequent re-fetch on the same line.
    if (
      noWellsAndSticks &&
      regionChanged &&
      availablePlays.length === 1 &&
      localSelectedPlays.length === 0 &&
      settings.selectedPlays.length === 0
    ) {
      setLocalSelectedPlays(availablePlays);
      setXdaSettings({ ...settings, selectedPlays: availablePlays });
      return;
    }

    // New region with multiple plays: always start with no selection so the
    // user must choose explicitly. Without this, a play auto-selected in a
    // previous single-play region would be filter-preserved here, appearing
    // as if it were auto-selected. Gated on regionChanged (line changed), so a
    // selection made on the current line is never cleared by its own re-fetch.
    if (regionChanged && availablePlays.length > 1 && localSelectedPlays.length > 0) {
      setLocalSelectedPlays([]);
      setXdaSettings({ ...settings, selectedPlays: [] });
      return;
    }

    if (localSelectedPlays.length > 0) {
      // Filter out plays that are no longer in the available list
      const filteredLocalSelectedPlays = localSelectedPlays.filter((item) =>
        availablePlays.includes(item)
      );
      if (filteredLocalSelectedPlays.length < localSelectedPlays.length) {
        setLocalSelectedPlays(filteredLocalSelectedPlays);
      }
      if (filteredLocalSelectedPlays.length === 0) {
        setXdaSettings({ ...settings, selectedPlays: [] });
      }
    }
    // Deps are only [availablePlays, noWellsAndSticks]: availablePlays changes on
    // every /line response (new array each time), which is exactly when we want to
    // re-evaluate. regionKey and settings.selectedPlays are intentionally
    // omitted — see the note above about the line/response timing, and so removing
    // a selection on the same line doesn't restart auto-select.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePlays, noWellsAndSticks]);

  useEffect(() => {
    if (!userSettings?.geoModelSettings?.organizationEnabled) return;
    const orgGroups = {
      label: "Organization",
      options: []
    };
    if (mapLayers?.length > 0) {
      for (const item of mapLayers) {
        if (orgGroups.options.findIndex((o) => o.value === item.group) === -1) {
          orgGroups.options.push({ label: item.group, value: item.group });
        }
      }
    }

    // if user is using mcdan env, make sure to show mcdan sources first
    const modelSources = IS_MCDAN_ENV
      ? [...mcdanielGroup, orgGroups]
      : [orgGroups, ...mcdanielGroup];

    setTopsModelSources(modelSources);
  }, [mapLayers, userSettings?.geoModelSettings?.organizationEnabled]);

  // sync option changes with context
  const handleSettingChange = (key: keyof XDASettingsT) => (value) => {
    let updatedValue = value;
    if (key === "scaleByValue") {
      //Convert value to 32-bit unsigned integer if handlesSettingChange is wrapped in inputWrapper. Math.min to ensure does not go above max if input is inputted via typing
      updatedValue = Math.min(RADIUS_DIMENSION_MAX, value * 1);
    }
    if (key !== "selectedPlays") {
      const nextSettings = { ...settings, [key]: updatedValue };
      setXdaSettings(nextSettings);
    } else {
      setLocalSelectedPlays(value);
      const nextSettings = {
        ...settings,
        selectedPlays: value
      };
      setXdaSettings(nextSettings);
    }
  };

  return (
    <XdaOptionsWrapper>
      <XdaOptionItem>
        <XdaOptionLabel>Scale Width</XdaOptionLabel>
        <Slider
          min={1}
          max={10}
          value={widthScaled}
          onChange={handleSettingChange("widthScaled")}
        />
      </XdaOptionItem>
      <XdaOptionItem>
        <XdaOptionLabel>Surfaces Source</XdaOptionLabel>
        <Select
          mode="multiple"
          size="small"
          defaultValue={[EVA_RESEARCH_NAME]}
          value={topsModelSource}
          options={topsModelSources}
          onChange={handleSettingChange("topsModelSource")}
        />
      </XdaOptionItem>
      {noWellsAndSticks && (
        <XdaOptionItem>
          <XdaOptionLabel>Selected Plays</XdaOptionLabel>
          <Select
            mode="multiple"
            size="small"
            value={localSelectedPlays?.map((item) => ({
              label: item,
              value: item
            }))}
            options={availablePlays.map((item) => ({
              label: item,
              value: item
            }))}
            onChange={handleSettingChange("selectedPlays")}
            disabled={isLoading}
          />
        </XdaOptionItem>
      )}
    </XdaOptionsWrapper>
  );
};

export default XdaSettingsOptions;
